import { useEffect, useLayoutEffect, useRef, type CSSProperties } from "react";
import { WifiOff } from "lucide-react";
import { useIsOnline } from "@/lib/reachability.ts";

// useLayoutEffect on the client (measure before paint); useEffect where there
// is no DOM (react-dom/server renders in the unit tests), which skips both.
const useIsoLayoutEffect = typeof window !== "undefined" ? useLayoutEffect : useEffect;

/** MA-750 — the layout reservation App.jsx applies while the banner shows.
 *
 *  The banner stays `position: fixed` at the top (always visible, never
 *  clipped by the session shell's `transform`), and the app frame moves
 *  down by the banner's MEASURED height so the banner covers only that
 *  reserved strip, never a control. On a 390px phone the copy wraps to two
 *  lines (48px) and, before MA-750, sat on the session Exit button (y=24..68)
 *  so an offline child could not leave the session.
 *
 *  - `spacerStyle`: an in-flow block of the banner's height, rendered before
 *    the frame (null → render no spacer).
 *  - `frameStyle`: for the `h-full` frames (hub, plain `.sb-root`), so
 *    spacer + frame still add up to the viewport.
 *  - `sessionFrameStyle`: for the session shell (`h-[100dvh]`, see
 *    FitToViewport), whose measured `clientHeight` is the space it fits to.
 *
 *  At 0 (online, or the banner hidden with CSS) every field is null or
 *  undefined, so the online DOM and layout are byte-identical to before. */
export function offlineInsetLayout(insetPx: number): {
  spacerStyle: CSSProperties | null;
  frameStyle: CSSProperties | undefined;
  sessionFrameStyle: CSSProperties | undefined;
} {
  const px = Number.isFinite(insetPx) ? Math.ceil(insetPx) : 0;
  if (px <= 0) return { spacerStyle: null, frameStyle: undefined, sessionFrameStyle: undefined };
  return {
    spacerStyle: { height: px, flexShrink: 0 },
    frameStyle: { height: `calc(100% - ${px}px)` },
    sessionFrameStyle: { height: `calc(100dvh - ${px}px)` },
  };
}

export const SAVING_MESSAGE = "Saving your progress… we'll keep trying.";

/** Soft top-of-app banner that surfaces when the browser goes offline.
 *  MA-347: `childName` parameterizes the copy off the ACTIVE child (never a
 *  hardcoded name — a stranger's name shown to every other family was the
 *  bug); falls back to neutral copy when there's no active child to name.
 *  `isOnline` is test-injectable (mirrors this repo's injectable-dependency
 *  test convention for small presentational banners) — production callers
 *  omit it and get the real, reachability-verified signal from
 *  `useIsOnline()`.
 *
 *  MA-750: it must never cover a tap target. `onHeightChange` reports the
 *  banner's rendered height (0 when it is not shown) so the app frame can
 *  reserve that strip (`offlineInsetLayout`). It is `pointer-events-none`
 *  (a passive status, never interactive) and sits at z-40, under modal
 *  overlays (z-50), which are full-screen tasks of their own. */
export function OfflineBanner({
  childName,
  isOnline,
  holdingWrites,
  onHeightChange,
}: {
  childName?: string | null;
  isOnline?: boolean;
  /** MA-107: ONLINE but the MA-15 outbox has been holding writes past its
   *  debounce (see outboxPending.ts) — shows the calm "Saving…" line. */
  holdingWrites?: boolean;
  onHeightChange?: (px: number) => void;
}) {
  const liveOnline = useIsOnline();
  const online = isOnline ?? liveOnline;
  const showSaving = online && !!holdingWrites;
  const showing = !online || showSaving;
  const ref = useRef<HTMLDivElement | null>(null);

  // Measure before paint so the frame moves in the same frame the banner
  // appears; re-measure when the copy re-wraps (rotation, resize).
  useIsoLayoutEffect(() => {
    if (!onHeightChange) return;
    const el = ref.current;
    if (!showing || !el) {
      onHeightChange(0);
      return;
    }
    const report = () => onHeightChange(Math.ceil(el.getBoundingClientRect().height));
    report();
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(report) : null;
    ro?.observe(el);
    return () => {
      ro?.disconnect();
      onHeightChange(0);
    };
  }, [showing, onHeightChange]);

  if (!showing) return null;

  const trimmedName = childName?.trim();
  // MA-107: honest because outboxDrain retries on mount, every verified
  // reconnect, tab foreground and every 5 minutes (outboxPending.ts).
  const message = showSaving
    ? SAVING_MESSAGE
    : trimmedName
    ? `You're offline — ${trimmedName}'s progress will sync when you reconnect.`
    : "You're offline — your progress will sync when you reconnect.";

  return (
    <div
      ref={ref}
      data-testid="offline-banner"
      data-banner-kind={showSaving ? "saving" : "offline"}
      role="status"
      aria-live="polite"
      // MA-760 — index.html is viewport-fit=cover, so in a home-screen install
      // the status bar can sit over the top of the page; the 8px (py-2) top
      // padding grows by the safe-area inset. The inset is 0 in a browser tab,
      // so the banner is unchanged there. The measured height (spacer) follows.
      style={{
        paddingTop: "calc(0.5rem + env(safe-area-inset-top, 0px))",
        // MA-833 — landscape notch: keep the text clear of the side insets.
        paddingLeft: "max(1rem, env(safe-area-inset-left, 0px))",
        paddingRight: "max(1rem, env(safe-area-inset-right, 0px))",
      }}
      className="pointer-events-none fixed inset-x-0 top-0 z-40 flex items-center justify-center gap-2 bg-terracotta px-4 pb-2 text-center text-xs font-bold text-cream shadow-soft"
    >
      {showSaving ? null : <WifiOff size={14} aria-hidden />}
      {message}
    </div>
  );
}
